import React, { useState, useCallback } from 'react';
import clsx from 'clsx';

/**
 * Tabs — Underline tab bar with state persistence (REQ-SYS-FND-01)
 *
 * Switches without losing state (parent manages content; Tabs only manages selection).
 * Active tab uses brand action color underline.
 *
 * Usage (controlled):
 *   <Tabs tabs={[{ value: 'details', label: 'Details' }, ...]} value={tab} onChange={setTab} />
 *
 * Usage (uncontrolled):
 *   <Tabs tabs={[...]} defaultValue="details" onChange={handleChange} />
 *
 * With counts:
 *   <Tabs tabs={[{ value: 'quotes', label: 'Quotes', count: 3 }]} ... />
 *
 * With attention dot (PT-19 2026-07-24):
 *   <Tabs tabs={[{ value: 'change-orders', label: 'Change Orders', dot: pendingCOs > 0 }]} ... />
 *   Renders a small red circle after the label; independent of `count` (both can coexist).
 */
export default function Tabs({ tabs = [], value, defaultValue, onChange, className }) {
  const [internal, setInternal] = useState(defaultValue || tabs[0]?.value);
  const activeValue = value !== undefined ? value : internal;

  const handleChange = useCallback((val) => {
    if (value === undefined) setInternal(val);
    onChange?.(val);
  }, [value, onChange]);

  return (
    <div
      role="tablist"
      className={clsx('flex items-center gap-6 border-b', className)}
      style={{ borderColor: 'var(--color-border)' }}
    >
      {tabs.map((t) => {
        const active = t.value === activeValue;
        return (
          <button
            key={t.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => handleChange(t.value)}
            className={clsx(
              'relative -mb-px py-2 text-sm font-medium',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)] rounded-sm',
              'transition-colors duration-150'
            )}
            style={{ color: active ? 'var(--color-text-default)' : 'var(--color-text-muted)' }}
          >
            <span className="flex items-center gap-1.5">
              {t.icon && <t.icon className="w-4 h-4" />}
              {t.label}
              {t.dot && (
                <span
                  aria-hidden="true"
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: 'var(--color-error)' }}
                />
              )}
              {t.count != null && t.count > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold"
                  style={{
                    background: active ? 'var(--color-action-primary)' : 'var(--color-border)',
                    color: active ? 'white' : 'var(--color-text-muted)',
                  }}
                >
                  {t.count}
                </span>
              )}
            </span>
            {active && (
              <span
                className="absolute left-0 right-0 -bottom-px h-0.5"
                style={{ background: 'var(--color-action-primary)' }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * TabPanel — Content panel for a tab (renders children only when active).
 * Use with keepMounted=true to preserve state when switching tabs.
 */
export function TabPanel({ value, activeValue, keepMounted = true, children }) {
  const active = value === activeValue;
  if (!keepMounted && !active) return null;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      className={active ? 'block' : 'hidden'}
    >
      {children}
    </div>
  );
}
